import { api } from '@aurora/convex/convex/_generated/api'
import { authReactClient } from '../sync/auth'
import { getToken } from '../sync/ConvexSync'
import { auroraStore } from '../store/auroraStore'

/**
 * A photo, from the parent's hand to the record.
 *
 * Three steps, none of them optional: ask the server for a one-shot upload
 * URL, put the bytes there, then tell the rail about the storage id. The
 * row comes back with its real id, so a message can attach it at once.
 */
export type PhotoPurpose = 'work' | 'sheet'

export async function uploadPhoto(file: File, opts: { childId: string; purpose: PhotoPurpose; title?: string }): Promise<string> {
  const token = getToken() ?? ''
  const url: string = await authReactClient.mutation(api.sync.generateUploadUrl, { token })
  const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': file.type || 'image/jpeg' }, body: file })
  if (!res.ok) throw new Error(`upload failed (${res.status})`)
  const { storageId } = (await res.json()) as { storageId: string }
  const dims = await imageSize(file).catch(() => undefined)
  const localUrl = URL.createObjectURL(file)
  const result = await (auroraStore.getState() as any).savePhoto({
    childId: opts.childId,
    storageId,
    mimeType: file.type || 'image/jpeg',
    bytes: file.size,
    width: dims?.width,
    height: dims?.height,
    title: opts.title ?? (opts.purpose === 'sheet' ? 'Filled-in sheet' : file.name.replace(/\.[a-z0-9]+$/i, '')),
    purpose: opts.purpose,
    localUrl,
  })
  return (result?.artifactId as string | undefined) ?? (result?.stubId as string)
}

function imageSize(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const src = URL.createObjectURL(file)
    img.onload = () => { resolve({ width: img.naturalWidth, height: img.naturalHeight }); URL.revokeObjectURL(src) }
    img.onerror = () => { reject(new Error('not an image')); URL.revokeObjectURL(src) }
    img.src = src
  })
}
